import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../context/AccountContext';
import { useTranslation } from "react-i18next";
import { useRequestVerifiedId } from '../../hooks/VerifiedIdHooks';
import { LockStatusResult } from '../../types/models';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { HelpMeAlert } from '../HelpMeAlert';
import { Button } from '@uhg-abyss/web/ui/Button';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Card } from '@uhg-abyss/web/ui/Card';
import { styled } from '@uhg-abyss/web/tools/styled';

const CardBlock = styled('div', { padding: '15px', margin: '4px' });

const QrCodeImg = styled('img', {
    width: 220,
    height: 220,
});

const formatCountdown = (remainingSeconds: number): string => {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = (remainingSeconds % 60)
        .toString()
        .padStart(2, '0');

    return `${minutes}:${seconds}`;
};

export const VerifiedId = () => {
    const { verifiedIdEnabled } = useAuth();
    const { lockStatus } = useAccounts();
    const { t } = useTranslation();

    const { mutate, data, isPending, isError } = useRequestVerifiedId();
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
    const isMobileOS = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
    );

    // Don't start issuance until the unlock check has resolved - matches the
    // "cannot start Verified ID setup if account is locked" requirement.
    useEffect(() => {
        if (lockStatus === LockStatusResult.NoAction || lockStatus === LockStatusResult.Unlocked) {
            mutate();
        }
    }, [lockStatus, mutate]);

    const expiry = data?.data.expiry;

    useEffect(() => {
        if (!expiry) {
            setRemainingSeconds(null);
            return;
        }

        const tick = () => setRemainingSeconds(Math.max(0, expiry - Math.floor(Date.now() / 1000)));
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [expiry]);

    if (!verifiedIdEnabled) {
        return <Navigate to="/home" replace />;
    }

    // Home's link into this page is disabled until the lock check resolves, so reaching
    // this point while still Pending only happens via direct URL navigation - send them back.
    if (lockStatus === LockStatusResult.Pending || lockStatus === LockStatusResult.Error) {
        return <Navigate to="/home" replace />;
    }

    const isLoading = isPending;
    const issuance = data?.data;
    // remainingSeconds starts null (no issuance yet) and only reaches exactly 0 once the
    // countdown effect's tick() has run past expiry - never true before an issuance exists.
    const isExpired = remainingSeconds === 0;

    return (
        <Layout.Stack alignLayout="center">
            <Heading level={3} color="$textPrimary" id="createVerifiedIdHeading" textAlign="center">
                {t('createVerifiedId')}
            </Heading>            
            <CardBlock>
                <Text color="$textPrimary" data-testid="createVerifiedIdDescription">
                    {t('createVerifiedIdDescription')}
                </Text>
            </CardBlock>
              
            {isError && (
                <HelpMeAlert status="error" title={t('createVerifiedIdError1')}>
                    <Text>{t('createVerifiedIdError2')}</Text>
                </HelpMeAlert>
            )}

            {isLoading && (
                <LoadingSpinner isLoading ariaLoadingLabel={t('createVerifiedIdLoadingMsg')} />
            )}

            {!isLoading && !isError && issuance && !isExpired && (
                <CardBlock>
                    <Card>
                        <Layout.Stack alignLayout="center">
                            <Text color="$textPrimary">{isMobileOS ? t('createVerifiedIdInstructionsMobile') : t('createVerifiedIdInstructionsNonMobile')}</Text>                            
                            <a href={issuance.deepLink} data-testid="verifiedIdDeepLinkAnchor">
                                <QrCodeImg src={issuance.qrCodeImage} alt={t('createVerifiedIdQrAlt')} />
                            </a>
                            <Text color="$textPrimary" data-testid="verifiedIdPin">
                                {t('createVerifiedIdPinLabel', { pin: issuance.pin })}
                            </Text>
                            {isMobileOS && (
                                <a href={issuance.deepLink} data-testid="verifiedIdLink">
                                    {t('createVerifiedIdLinkLabel')}
                                </a>
                            )}
                            {remainingSeconds !== null && (
                                <Text color="$textPrimary" data-testid="verifiedIdCountdown">
                                    {t('createVerifiedIdCountdown', { countdown: formatCountdown(remainingSeconds) })}
                                </Text>
                            )}
                        </Layout.Stack>
                    </Card>
                </CardBlock>
            )}

            {!isLoading && !isError && issuance && isExpired && (
                <HelpMeAlert status="warning" title={t('createVerifiedIdExpiredMsg')}>
                    <Layout.Stack alignLayout="center">
                        <Button onClick={() => mutate()} data-testid="verifiedIdRefreshBtn">{t('tryAgain')}</Button>
                    </Layout.Stack>
                </HelpMeAlert>
            )}
        </Layout.Stack>
    );
};
